import { utils } from "ethers";

/**
 * Global cache for market decimals, populated from /markets API response.
 * Key: instrument_name (e.g., "WNATIVE-MUSDC") or instrument_id
 * Value: { baseDecimals, quoteDecimals }
 */
export interface MarketDecimals {
  baseDecimals: number;
  quoteDecimals: number;
}

const marketDecimalsCache: Record<string, MarketDecimals> = {};

/**
 * Subscribers to re-read the registry when it changes.
 *
 * This store is written by an SWR fetcher side effect, long after the first
 * components have rendered. It is a plain module object, so nothing re-rendered
 * when it filled: a book converted before `/markets` resolved kept its wrong
 * scaling forever. Callers subscribe via `useMarketDecimals`.
 */
const listeners = new Set<() => void>();

/** Subscribe to registry changes. Returns an unsubscribe function. */
export const subscribeMarketDecimals = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Register market decimals from API response.
 * Called when markets data is fetched.
 */
export const registerMarketDecimals = (
  instrumentKey: string,
  baseDecimals: number,
  quoteDecimals: number
) => {
  const existing = marketDecimalsCache[instrumentKey];
  if (
    existing &&
    existing.baseDecimals === baseDecimals &&
    existing.quoteDecimals === quoteDecimals
  ) {
    // Re-registering the same values must not notify: `useSyncExternalStore`
    // would otherwise see a new snapshot on every markets refetch.
    return;
  }
  marketDecimalsCache[instrumentKey] = { baseDecimals, quoteDecimals };
  marketDecimalsCache[instrumentKey.toLowerCase()] = {
    baseDecimals,
    quoteDecimals,
  };
  listeners.forEach((listener) => listener());
};

/**
 * Decimals for a market, or `undefined` when they are not known yet.
 *
 * This used to answer `{18, 6}` for any miss. That guess is wrong for perps
 * (which are 18/18), and because the answer was indistinguishable from a real
 * one, every caller converted with it and showed the result as fact — prices
 * out by a factor of 10^12. Unknown decimals mean a value CANNOT be converted;
 * callers must wait rather than guess.
 */
export const getMarketDecimals = (
  instrumentKey: string
): MarketDecimals | undefined =>
  marketDecimalsCache[instrumentKey] ||
  marketDecimalsCache[instrumentKey.toLowerCase()];

/**
 * Convert a raw on-chain integer to a human-readable decimal string.
 * e.g., rawToDecimal("23029000000000000", 18) => "0.023029"
 */
export const rawToDecimal = (
  rawValue: string | number,
  decimals: number
): string => {
  if (!rawValue || rawValue === "0" || rawValue === 0) return "0";
  try {
    const str = rawValue.toString().split(".")[0];
    return utils.formatUnits(str, decimals);
  } catch {
    return "0";
  }
};

/**
 * Check if a value looks like a raw on-chain integer.
 * Raw values are integers (no decimal point) with magnitude suggesting they haven't been converted.
 * A value like "23029" could be either raw (6-dec price) or human-readable.
 * We check: if it's a string without a decimal point AND length > 4, treat as raw.
 * Values with decimal points are already human-readable.
 */
export const isRawValue = (value: string | number): boolean => {
  const str = value.toString();
  // If it contains a decimal point, it's already human-readable
  if (str.includes(".")) return false;
  const num = Number(value);
  // Very large integers are definitely raw
  if (!isNaN(num) && Math.abs(num) > 1e12) return true;
  // For smaller integers, we can't be sure — return false and let the caller decide
  return false;
};

/**
 * Display price for a trade whose wire format may be RAW (x10^quoteDecimals)
 * or already DECIMAL — both reach the client today (REST perp trades are
 * decimal, REST spot trades were raw, websocket trades are raw).
 *
 * The old test was "an integer string of 10+ digits is raw". That holds for
 * 18dp quotes and for 6dp quotes above $10,000, and fails below: Monad
 * MON-USDC sent "22900" for $0.0229 and the trade panel and the order book's
 * "Last" rendered $22,900.0000.
 *
 * With a reference price for the market (its mark or index), pick whichever
 * reading is closer to it on a log scale: the two candidates differ by
 * 10^quoteDecimals, so the right one is unambiguous. Without a reference, keep
 * the 10-digit rule.
 */
export const toDisplayTradePrice = (
  value: string | number | undefined | null,
  quoteDecimals: number,
  referencePrice?: string | number
): string => {
  if (value === undefined || value === null) return "";
  const str = String(value);
  if (!/^\d+$/.test(str)) return str; // decimal, signed, empty or non-numeric
  const ref = Number(referencePrice);
  if (Number.isFinite(ref) && ref > 0) {
    const scaled = rawToDecimal(str, quoteDecimals);
    const distance = (v: number) => (v > 0 ? Math.abs(Math.log10(v / ref)) : Infinity);
    return distance(Number(scaled)) < distance(Number(str)) ? scaled : str;
  }
  return /^\d{10,}$/.test(str) ? rawToDecimal(str, quoteDecimals) : str;
};

/**
 * Convert raw orderbook price to display price.
 *
 * CLOB price formula: quoteAmount = baseAmount * price / 10^baseDecimals
 * Price is scaled to quoteDecimals (e.g., 6 for MUSDC).
 *
 * Display price = rawPrice / 10^quoteDecimals
 *
 * Example: WNATIVE-MUSDC (quoteDecimals=6)
 *   rawPrice = 23029
 *   displayPrice = 23029 / 10^6 = 0.023029
 */
export const convertOrderbookPrice = (
  rawPrice: string | number,
  _baseDecimals: number,
  quoteDecimals: number
): string => {
  if (!rawPrice || rawPrice === "0" || rawPrice === 0) return "0";
  return rawToDecimal(rawPrice, quoteDecimals);
};

/**
 * Convert raw orderbook quantity to display quantity.
 * Quantity is in base token units, so divide by 10^baseDecimals.
 *
 * Example: rawQty = 169000000000000000 with baseDecimals=18
 *   displayQty = 0.169
 */
export const convertOrderbookQuantity = (
  rawQty: string | number,
  baseDecimals: number
): string => {
  return rawToDecimal(rawQty, baseDecimals);
};

/**
 * Convert mark_price from the indexer (token0Price).
 *
 * The indexer computes token0Price = rawPrice / 10^quoteDecimals (with float precision loss).
 * To get human display price, we need to undo the remaining factor:
 *   displayPrice = token0Price / 10^(baseDecimals - quoteDecimals)
 *
 * However, due to float32 precision issues in the indexer, this value may be unreliable.
 * Callers should prefer orderbook mid price when available.
 */
export const convertMarkPrice = (
  rawMarkPrice: string | number,
  baseDecimals: number,
  quoteDecimals: number
): string => {
  if (!rawMarkPrice || rawMarkPrice === "0" || rawMarkPrice === 0) return "0";
  const remaining = baseDecimals - quoteDecimals;
  if (remaining <= 0) return rawMarkPrice.toString();
  return rawToDecimal(rawMarkPrice, remaining);
};
